"use client"

import { useTranslations } from "next-intl"
import { useCallback, useEffect, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { Clock } from "lucide-react"
import { CommerceIcons } from "@/lib/icons/commerce-icons"
import { createClient } from "@/utils/supabase/client"
import { DIALOG_CARD_BODY_CLASS } from "@/lib/dialog-styles"
import { DialogCard } from "@/components/ui/dialog-card"
import { DockSheet } from "@/components/ui/dock-sheet"
import { Button } from "@/components/ui/button"
import { useMobile } from "@/hooks/use-mobile"
import { cn, formatPrice } from "@/lib/utils"
import { SHEET_Z_NESTED } from "@/lib/sheet-styles"

interface UnpaidAuction {
  id: string
  title: string
  imageUrl: string | null
  currency: string
  winningBid: number
  paymentDeadline: string | null
}

const SESSION_KEY = "unpaid-auctions-dismissed"

function useFormatTimeLeft() {
  const t = useTranslations("unpaidAuctions")
  return (deadline: string | null): string => {
    if (!deadline) return ""
    const diff = new Date(deadline).getTime() - Date.now()
    if (diff <= 0) return t("expiringSoon")
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    if (hours >= 24) {
      const days = Math.floor(hours / 24)
      return t("daysLeft", { days, hours: hours % 24 })
    }
    return t("timeLeft", { hours, minutes })
  }
}

export function UnpaidAuctionsReminder() {
  const t = useTranslations("unpaidAuctions")
  const formatTimeLeft = useFormatTimeLeft()
  const isMobile = useMobile()
  const [open, setOpen] = useState(false)
  const [auctions, setAuctions] = useState<UnpaidAuction[]>([])
  const [userId, setUserId] = useState<string | null>(null)
  const fetchedRef = useRef(false)

  // Track auth state
  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => setUserId(user?.id ?? null))
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null)
    })
    return () => subscription.unsubscribe()
  }, [])

  // Fetch unpaid auctions after a delay (not on first paint)
  useEffect(() => {
    if (!userId) return
    if (fetchedRef.current) return

    // Don't show if already dismissed this session
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return
    } catch {
      /* SSR or private browsing */
    }

    const timer = setTimeout(async () => {
      fetchedRef.current = true
      try {
        const res = await fetch("/api/auctions/unpaid")
        if (!res.ok) return
        const data = await res.json()
        if (data.auctions?.length > 0) {
          setAuctions(data.auctions)
          setOpen(true)
        }
      } catch {
        /* network error, silently fail */
      }
    }, 2000)

    return () => clearTimeout(timer)
  }, [userId])

  const handleDismiss = useCallback(() => {
    setOpen(false)
    try {
      sessionStorage.setItem(SESSION_KEY, "1")
    } catch {
      /* ignore */
    }
  }, [])

  const handleItemClick = useCallback(() => {
    handleDismiss()
  }, [handleDismiss])

  if (auctions.length === 0) return null

  // The header row was a real chrome title (`text-lg` + close as its flex
  // sibling), so it moves to `title`/`belowTitle` and BOTH branches paint it
  // from the primitive. The bid glyph rides along with the count line rather
  // than being dropped — the chrome row has no icon slot by design, and an
  // open slot is what manufactured the drift this phase removes
  // (HANDOFF_DIALOG_CHROME §3-§4).
  const belowTitle = (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/20">
        <CommerceIcons.bid className="h-5 w-5 text-amber-400" />
      </div>
      <p className="text-sm text-muted-foreground">
        {auctions.length === 1
          ? t("singularMessage")
          : t("pluralMessage", { count: auctions.length })}
      </p>
    </div>
  )

  const content = (
    <div className="flex flex-col min-h-0">
      <div
        className={cn(
          DIALOG_CARD_BODY_CLASS,
          "pt-2",
          isMobile && "px-4 pb-3 max-h-[55vh] overflow-y-auto scrollbar-none [&::-webkit-scrollbar]:hidden"
        )}
      >
        <div className="space-y-2.5">
          {auctions.map((auction) => (
            <Link
              key={auction.id}
              href={`/listings/${auction.id}`}
              onClick={handleItemClick}
              className="group flex items-center gap-3.5 rounded-2xl border border-foreground/8 bg-foreground/5 p-3.5 transition-colors hover:bg-foreground/10"
            >
              <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-muted">
                {auction.imageUrl ? (
                  <Image
                    src={auction.imageUrl}
                    alt={auction.title}
                    fill
                    className="object-cover"
                    sizes="56px"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <CommerceIcons.bid className="w-5 h-5 text-muted-foreground" />
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="truncate text-[17px] font-semibold text-foreground">{auction.title}</p>
                <p className="mt-0.5 text-[38px] leading-none font-bold text-amber-400">
                  {formatPrice(auction.winningBid, auction.currency)}
                </p>
                {auction.paymentDeadline && (
                  <div className="mt-1 flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {formatTimeLeft(auction.paymentDeadline)}
                    </span>
                  </div>
                )}
              </div>

              <div className="shrink-0 text-muted-foreground/70 transition-colors group-hover:text-foreground">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="-rotate-90"
                >
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* No mobile `pb-*`: inside the sheet the primitive owns the bottom
          inset (1.5rem + safe-area). Desktop keeps `pb-5`. */}
      <div className={cn("px-5 pb-5 pt-2", isMobile && "px-4 pb-0")}>
        <Button
          variant="ghost"
          className="h-12 w-full rounded-full text-lg font-semibold text-foreground/80 hover:bg-foreground/5 hover:text-foreground"
          onClick={handleDismiss}
        >
          {t("remindLater")}
        </Button>
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <DockSheet
        open={open}
        onOpenChange={(o) => {
          if (!o) handleDismiss()
        }}
        title={t("title")}
        titleAlign="left"
        belowTitle={belowTitle}
        // `content` caps its own list at `max-h-[55vh]` and scrolls it, which
        // is what makes the hug tier safe here: the hug branch of the sheet
        // does NOT scroll its body.
        contentSized
        zIndex={SHEET_Z_NESTED}
        contentClassName="px-0 pt-0"
      >
        {content}
      </DockSheet>
    )
  }

  return (
    <DialogCard
      open={open}
      onOpenChange={(o) => {
        if (!o) handleDismiss()
      }}
      title={t("title")}
      titleAlign="left"
      belowTitle={belowTitle}
      panelClassName="max-w-2xl"
      // `content` owns its own padding and its own capped scroll region (it is
      // shared with the sheet branch), so the card's body contributes none.
      contentClassName="px-0 pt-0"
    >
      {content}
    </DialogCard>
  )
}
